/*
  PDP Size Fit Guarantee — page behaviour.

  One custom element:

    <v2-pdp-size-fit>   the height filter, the video rail, the lightbox, and the
                     bridge to Sizebay's fit finder

  ---------- Where this came from, and why it is a copy ----------

  Forked from the TravelBundleFit class in assets/travel-bundle.js at commit
  9e64787, renamed to PdpSizeFit and registered as <v2-pdp-size-fit>.

  It is a deliberate copy rather than a shared script. The Europe-Ready Travel
  Bundle page is the destination for paid cold traffic, and a file shared with a
  PDP means a change made for one silently moves the other, in a repo with no
  test suite to catch it.

  The consequence, stated plainly: a bug fixed in one of these two files is NOT
  fixed in the other. Both classes are addressed entirely through data-attributes
  and neither depends on a class name, so they should stay easy to diff — keep
  any change here structural-for-structural with its counterpart.

  Custom element rather than a DOMContentLoaded pass, so it upgrades correctly
  when the theme editor re-renders the section.
*/

(() => {
  'use strict';

  class PdpSizeFit extends HTMLElement {
    connectedCallback() {
      this.chips = Array.from(this.querySelectorAll('[data-fit-filter]'));
      this.items = Array.from(this.querySelectorAll('[data-height]'));
      this.status = this.querySelector('[data-fit-status]');

      this.rail = this.querySelector('[data-fit-rail]');
      this.nav = this.querySelector('[data-fit-nav]');
      this.prev = this.querySelector('[data-fit-prev]');
      this.next = this.querySelector('[data-fit-next]');

      this.dialog = this.querySelector('[data-fit-dialog]');
      this.video = this.querySelector('[data-fit-video]');
      this.dialogCaption = this.querySelector('[data-fit-dialog-caption]');
      this.triggers = Array.from(this.querySelectorAll('[data-fit-play]'));

      this._onChip = this.onChip.bind(this);
      this._onPlay = this.onPlay.bind(this);
      this._onClose = this.close.bind(this);
      this._onDialogClose = this.onDialogClose.bind(this);
      this._onBackdrop = this.onBackdrop.bind(this);

      this.chips.forEach((chip) => chip.addEventListener('click', this._onChip));
      this.triggers.forEach((trigger) => trigger.addEventListener('click', this._onPlay));

      this.setupRail();

      this.querySelectorAll('[data-fit-close]').forEach((button) =>
        button.addEventListener('click', this._onClose)
      );

      if (this.dialog) {
        // `close` also fires for Esc, which is the whole reason for using
        // <dialog> — one teardown path regardless of how it was dismissed.
        this.dialog.addEventListener('close', this._onDialogClose);
        this.dialog.addEventListener('click', this._onBackdrop);
      }

      this.setupSizebay();
    }

    disconnectedCallback() {
      this.chips?.forEach((chip) => chip.removeEventListener('click', this._onChip));
      this.triggers?.forEach((trigger) => trigger.removeEventListener('click', this._onPlay));

      this.querySelectorAll('[data-fit-close]').forEach((button) =>
        button.removeEventListener('click', this._onClose)
      );

      if (this.dialog) {
        this.dialog.removeEventListener('close', this._onDialogClose);
        this.dialog.removeEventListener('click', this._onBackdrop);
      }

      this.openers?.forEach((button) => button.removeEventListener('click', this._onSizebay));
      clearInterval(this.sizebayPoll);
      this.releaseScrollHold();

      this.rail?.removeEventListener('scroll', this._onRailScroll);
      this.prev?.removeEventListener('click', this._onPrev);
      this.next?.removeEventListener('click', this._onNext);
      this.railResize?.disconnect();
      if (this.railFrame) cancelAnimationFrame(this.railFrame);
    }

    /* --------------------------------------------------------- Video rail */

    /**
     * The rail scrolls without any of this. What the platform will not give us
     * is a pair of arrows that know when they have run out of cards, so that is
     * all this adds — plus resetting the scroll position when the filter
     * changes, since a rail left scrolled to its end shows nothing at all once
     * it has been refilled with three cards.
     */
    setupRail() {
      if (!this.rail) return;

      this._onRailScroll = this.onRailScroll.bind(this);
      this._onPrev = () => this.stepRail(-1);
      this._onNext = () => this.stepRail(1);

      this.rail.addEventListener('scroll', this._onRailScroll, { passive: true });
      this.prev?.addEventListener('click', this._onPrev);
      this.next?.addEventListener('click', this._onNext);

      // Card width and how many fit both change with the viewport, and so does
      // whether the rail overflows at all. ResizeObserver rather than a window
      // listener because the panel is inside a container that can change width
      // without the window doing so — the theme editor resizes its preview.
      if (window.ResizeObserver) {
        this.railResize = new ResizeObserver(() => this.syncRail());
        this.railResize.observe(this.rail);
      }

      this.syncRail();
    }

    /**
     * One card short of a full screen, so the card that was cut off at the edge
     * is fully in view after a press and stays as the visual link between the
     * two positions. Paging by the exact viewport width would scroll that card
     * straight back out again, and there is no way to tell a rail that jumped a
     * whole screen from one that jumped two.
     */
    stepRail(direction) {
      if (!this.rail) return;

      const card = this.items.find((item) => !item.hidden);
      const stride = card ? card.getBoundingClientRect().width + this.railGap() : 200;
      const page = Math.max(stride, this.rail.clientWidth - stride);

      // Smooth is requested here rather than declared as scroll-behavior on the
      // rail, because the filter reset scrolls the same element and has to land
      // instantly — a declared smooth would animate that too, and a rail gliding
      // back from its end while its contents are being swapped underneath reads
      // as a glitch rather than as a reset.
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.rail.scrollBy({ left: direction * page, behavior: motion ? 'auto' : 'smooth' });
    }

    railGap() {
      return parseFloat(getComputedStyle(this.rail).columnGap) || 0;
    }

    /**
     * Reveals the arrows only when there is something off-screen to reach, and
     * disables each one at its end.
     *
     * The 2px tolerance is for subpixel widths: a rail scrolled fully right
     * routinely lands a fraction of a pixel short of scrollWidth, which would
     * otherwise leave the forward arrow enabled and doing nothing.
     */
    syncRail() {
      if (!this.rail) return;

      const overflow = this.rail.scrollWidth - this.rail.clientWidth;
      const scrollable = overflow > 2;

      if (this.nav) this.nav.hidden = !scrollable;
      if (!scrollable) return;

      const x = this.rail.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 2;
      if (this.next) this.next.disabled = x >= overflow - 2;
    }

    onRailScroll() {
      // Coalesced to a frame: scroll fires far more often than the disabled
      // state can meaningfully change, and each pass reads layout.
      if (this.railFrame) return;

      this.railFrame = requestAnimationFrame(() => {
        this.railFrame = null;
        this.syncRail();
      });
    }

    /* ------------------------------------------------------ Sizebay fitter */

    /**
     * Wires this card's buttons to Sizebay's fit finder.
     *
     * Sizebay mounts its own buttons once per page — its module resolves the
     * anchor with querySelector — and on this page that mount is the buy box.
     * Rather than compete for it, these buttons post the messages Sizebay's own
     * photo overlay uses (`szb-open-fitting-room`, `szb-open-measurements`),
     * which it handles by clicking whichever real button matches. Same modal,
     * from anywhere on the page.
     */
    setupSizebay() {
      this.fitter = this.querySelector('[data-fit-sizebay]');
      if (!this.fitter) return;

      this.fallback = this.querySelector('[data-fit-sizebay-fallback]');
      this.openers = Array.from(this.querySelectorAll('[data-fit-sizebay-open]'));

      this._onSizebay = this.onSizebay.bind(this);
      this.openers.forEach((button) => button.addEventListener('click', this._onSizebay));

      this.whenSizebayReady();
    }

    /**
     * Sizebay loads its module, resolves the product against its own catalogue
     * and only then renders anything, so the buttons cannot be shown on trust.
     * Presence of #szb-vfr-button is the one honest signal that the modal these
     * buttons open actually exists.
     *
     * Give up after fifteen seconds. Sizebay fails outright when it cannot
     * resolve the product — which is every local dev server, since the
     * permalink it builds carries the dev host — and in that state the fallback
     * link is the correct thing to be left with rather than a control that
     * opens nothing.
     */
    whenSizebayReady() {
      const mounted = () => document.getElementById('szb-vfr-button');

      if (mounted()) {
        this.revealSizebay();
        return;
      }

      let waited = 0;

      this.sizebayPoll = setInterval(() => {
        waited += 250;

        if (mounted()) {
          clearInterval(this.sizebayPoll);
          this.revealSizebay();
        } else if (waited >= 15000) {
          clearInterval(this.sizebayPoll);
        }
      }, 250);
    }

    revealSizebay() {
      this.fitter.hidden = false;

      // The two would otherwise say the same thing twice, and the fallback is
      // the weaker of them — it only points at the page the finder is on.
      if (this.fallback) this.fallback.hidden = true;
    }

    onSizebay(event) {
      const message = event.currentTarget.dataset.fitSizebayOpen;
      if (!message) return;

      this.holdScrollThroughSizebay();
      window.postMessage(message, window.location.origin);
    }

    /**
     * Keeps the page where she left it when Sizebay's modal closes.
     *
     * Sizebay mounts its real buttons once per page, at the buy box, and the
     * buttons in this section work by asking it to click those. When the modal
     * closes it calls focus() on its own container — #szb-container, which sits
     * inside #build-your-bundle — and the browser scrolls that into view.
     * Measured on the live page: closing from this section moved the window from
     * 5648 to 661, about five thousand pixels up, for the measurements chart and
     * the fitting room alike. Read from here it looks like the page throwing her
     * back to the buy box for pressing Size guide.
     *
     * So the position is taken before the modal opens and put back once it has
     * gone. Keyed to Sizebay's own `vfr-modal-active` class on <html> rather
     * than to a close button, because the modal is an iframe on their domain
     * and can be dismissed from inside it, by the backdrop, or by finishing the
     * flow — the class going away is the one signal common to all of them.
     *
     * Esc does not close it. That is Sizebay's behaviour inside their iframe and
     * not something this can reach.
     */
    holdScrollThroughSizebay() {
      const root = document.documentElement;
      const ACTIVE_CLASS = 'vfr-modal-active';

      // Long enough for their iframe to load on a slow connection. If the modal
      // never opens there is nothing to correct, and the hold has to let go —
      // otherwise it fires against whatever she scrolled to minutes later.
      const OPEN_TIMEOUT = 6000;

      // The focus, and the scroll it causes, do not necessarily land in the same
      // frame as the class change, so the position is reasserted across a short
      // window rather than once.
      const SETTLE_MS = 400;

      this.releaseScrollHold();

      const target = window.scrollY;
      let opened = root.classList.contains(ACTIVE_CLASS);

      const settle = () => {
        // Any genuine input means she has taken the page over; a script that
        // keeps yanking it back at that point is worse than the jump.
        const surrender = () => this.releaseScrollHold();
        const events = ['wheel', 'touchstart', 'keydown'];
        events.forEach((type) =>
          window.addEventListener(type, surrender, { passive: true })
        );

        if (this.scrollHold) this.scrollHold.surrender = { surrender, events };

        const until = performance.now() + SETTLE_MS;

        const tick = () => {
          if (!this.scrollHold) return;

          if (Math.round(window.scrollY) !== Math.round(target)) {
            window.scrollTo(0, target);
          }

          if (performance.now() < until) {
            this.scrollHold.frame = requestAnimationFrame(tick);
          } else {
            this.releaseScrollHold();
          }
        };

        tick();
      };

      const observer = new MutationObserver(() => {
        if (root.classList.contains(ACTIVE_CLASS)) {
          opened = true;
          return;
        }

        if (opened) settle();
      });

      observer.observe(root, { attributes: true, attributeFilter: ['class'] });

      this.scrollHold = {
        observer,
        frame: 0,
        surrender: null,
        timeout: setTimeout(() => {
          if (!opened) this.releaseScrollHold();
        }, OPEN_TIMEOUT),
      };
    }

    releaseScrollHold() {
      if (!this.scrollHold) return;

      const { observer, frame, timeout, surrender } = this.scrollHold;

      // Cleared before the listeners so a queued frame sees no hold and stops.
      this.scrollHold = null;

      observer.disconnect();
      cancelAnimationFrame(frame);
      clearTimeout(timeout);

      if (surrender) {
        surrender.events.forEach((type) =>
          window.removeEventListener(type, surrender.surrender)
        );
      }
    }

    onChip(event) {
      const chip = event.currentTarget;
      const range = chip.dataset.fitFilter;

      this.chips.forEach((other) =>
        other.setAttribute('aria-pressed', String(other === chip))
      );

      let shown = 0;
      this.items.forEach((item) => {
        const match = range === 'all' || item.dataset.height === range;
        item.hidden = !match;
        if (match) shown += 1;
      });

      // Back to the first card. A rail left scrolled to its far end would
      // otherwise be sitting past the last of the three cards that replaced the
      // ten, so pressing a chip would appear to empty the panel.
      if (this.rail) {
        this.rail.scrollLeft = 0;
        this.syncRail();
      }

      if (this.status) {
        const label = chip.textContent.trim();
        this.status.textContent =
          shown === 1 ? `1 video, ${label}` : `${shown} videos, ${label}`;
      }
    }

    onPlay(event) {
      if (!this.dialog || !this.video) return;

      const trigger = event.currentTarget;
      const src = trigger.dataset.video;
      if (!src) return;

      // Remembered so focus can be handed back to the exact thumbnail that
      // opened the dialog, rather than to the top of the section.
      this.lastTrigger = trigger;

      const poster = trigger.dataset.poster;
      if (poster) this.video.setAttribute('poster', poster);

      this.video.src = src;

      if (this.dialogCaption) {
        this.dialogCaption.textContent = trigger.dataset.caption || '';
      }

      this.dialog.showModal();

      // Autoplay can be refused (low power mode, permissions). The controls are
      // already there, so a rejected promise is not an error worth surfacing.
      this.video.play?.().catch(() => {});
    }

    close() {
      this.dialog?.close();
    }

    /**
     * Clicks land on the dialog element itself only when they miss the inner
     * panel, which is how a backdrop click is detected without adding a
     * separate overlay element to trap it.
     */
    onBackdrop(event) {
      if (event.target === this.dialog) this.close();
    }

    onDialogClose() {
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.removeAttribute('poster');
        this.video.load();
      }

      this.lastTrigger?.focus();
      this.lastTrigger = null;
    }
  }

  /**
   * <travel-bundle-photos> — full-size viewing for customer review photos.
   *
   * The thumbnails on the quote cards are cropped hard to 3:4 so every card sits
   * level with its neighbours, which means the crop discards part of each photo —
   * frequently the part showing the garment on the person. This gives that back.
   *
   * Same shape as the fit-video dialog: one native <dialog> reused for every
   * thumbnail, src assigned on open and cleared on close. Clearing matters more
   * here than it looks — these are unresized Loox originals, and leaving one
   * decoded in memory after closing costs several megabytes on a phone for an
   * image nobody is looking at.
   *
   * With the script blocked the thumbnails are inert buttons. That is why the
   * section only asks the quote snippet for buttons when it is also rendering the
   * dialog: the failure mode is a photo you cannot enlarge, not a control that
   * silently does nothing where a working one is implied.
   */

  if (!window.customElements.get('v2-pdp-size-fit')) {
    window.customElements.define('v2-pdp-size-fit', PdpSizeFit);
  }
})();
